// src/components/Home.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaMusic,
  FaVideo,
  FaPlayCircle,
  FaBookReader,
  FaChalkboardTeacher,
  FaChartPie,
  FaPrayingHands,
  FaListUl,
  FaCalendarAlt,
  FaStepBackward,
  FaPlay,
  FaPause,
  FaStepForward,
  FaVolumeMute,
  FaVolumeUp,
  FaUsers,
  FaTv,
  FaQuestionCircle,
  FaWaveSquare,
  FaHeadphones,
  FaBook,
  FaComments,
  FaPodcast,
  FaBlog,
  FaStore,
  FaMedal,
  FaFacebook,
  FaHeadset,
  FaUserTie,
  FaHandsHelping,
  FaUser,
  FaChevronDown,
  FaChevronUp,
  FaHeart,
  FaCross,
  FaShieldAlt,
  FaGift,
  FaLaughBeam,
  FaWalking,
  FaChurch,
  FaGlobe,
  FaLeaf,
  FaBullhorn,
  FaSeedling,
  FaUserFriends,
  FaHandshake,
  FaPeace,
  FaDumbbell,
  FaCoins,
  FaFeather,
  FaFeatherAlt,
  FaSun,
  FaTshirt,
  FaHourglass,
  FaBreadSlice,
  FaBalanceScale,
  FaBookOpen,
  FaHands,
  FaGavel,
  FaHandHoldingHeart,
  FaAppleAlt,
  FaEye,
  FaSearch,
  FaSmile,
  FaShoppingCart,
} from "react-icons/fa";
import "./Home.css"; // Import specific styles
import SEO from './components/SEO';
import { loadYouTubeIframeAPI } from "./utils/youtube";
import localVideos from "./assets/livestreaming/videos.json";
import playlistsData from "./assets/livestreaming/playlists.json";

const shuffleArray = (list) => {
  const clone = [...list];
  for (let i = clone.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [clone[i], clone[j]] = [clone[j], clone[i]];
  }
  return clone;
};

const ICON_GRADIENTS = [
  { start: "#7c3aed", end: "#a855f7" },
  { start: "#ec4899", end: "#f472b6" },
  { start: "#0ea5e9", end: "#38bdf8" },
  { start: "#14b8a6", end: "#22d3ee" },
  { start: "#f97316", end: "#facc15" },
  { start: "#38bdf8", end: "#6366f1" },
  { start: "#34d399", end: "#059669" },
  { start: "#22d3ee", end: "#3b82f6" },
];

const PRIMARY_APP_LINKS = [
  { id: "worship", label: "Worship", to: "/worship", icon: FaPrayingHands },
  { id: "bible", label: "Bible", to: "/bibleis", icon: FaBookReader },
  { id: "videos", label: "Videos", to: "/video", icon: FaVideo },
  { id: "audio", label: "Audio", to: "/audio", icon: FaHeadphones },
  { id: "tv", label: "GCC TV", to: "/live", icon: FaTv },
  { id: "present", label: "Present", to: "/present", icon: FaChartPie },
  { id: "session", label: "Session", to: "/session", icon: FaListUl },
  { id: "dj", label: "DJ Mixer", to: "/dj", icon: FaWaveSquare },
  { id: "music", label: "Music", to: "/music", icon: FaMusic },
  { id: "events", label: "Events", to: "/events", icon: FaCalendarAlt },
  { id: "store", label: "Store", to: "/store", icon: FaShoppingCart },
  { id: "songs", label: "Songs", to: "/songs", icon: FaPlayCircle },
  { id: "karaoke", label: "Karaoke", to: "/karaoke", icon: FaMusic },
  { id: "messages", label: "Messages", to: "/messages", icon: FaChalkboardTeacher },
  { id: "help", label: "Help", to: "/help", icon: FaQuestionCircle },
  { id: "join", label: "Join Us", href: "https://www.youtube.com/@gcc-worship?sub_confirmation=1", icon: FaUsers },
];

const EXTRA_APP_LINKS = [
  { id: "playlists", label: "Playlists", to: "/playlists", icon: FaListUl },
  { id: "resources", label: "Resources", to: "/resources", icon: FaBook },
  { id: "bible-study", label: "Bible Study", to: "/biblestudy", icon: FaBookReader },
  { id: "community", label: "Community", to: "/community", icon: FaUsers },
  { id: "volunteer", label: "Volunteer", to: "/volunteer", icon: FaHandsHelping },
  { id: "forums", label: "Forums", to: "/forums", icon: FaComments },
  { id: "prayer", label: "Prayer Wall", to: "/prayerwall", icon: FaPrayingHands },
  { id: "profile", label: "Profile", to: "/profile", icon: FaUser },
  { id: "podcasts", label: "Podcasts", to: "/podcasts", icon: FaPodcast },
  { id: "blog", label: "Blog", to: "/blog", icon: FaBlog },
  { id: "marketplace", label: "Marketplace", to: "/marketplace", icon: FaStore },
  { id: "gamification", label: "Gamification", to: "/gamification", icon: FaMedal },
  { id: "social", label: "Social Media", to: "/socialmedia", icon: FaFacebook },
  { id: "counseling", label: "Counseling", to: "/counseling", icon: FaHeadset },
  { id: "mentorship", label: "Mentorship", to: "/mentorship", icon: FaUserTie },
];

const APP_LINKS = [...PRIMARY_APP_LINKS, ...EXTRA_APP_LINKS];

const HIDDEN_PLAYER_VARS = {
  autoplay: 1,
  controls: 0,
  rel: 0,
  modestbranding: 1,
  disablekb: 1,
  playsinline: 1,
};

const HERO_WORDS_RAW = [
  { text: "Love your neighbor", x: -120, y: -110, hue: 340, icon: FaHandsHelping },
  { text: "Pray without ceasing", x: 130, y: -90, hue: 45, icon: FaPrayingHands },
  { text: "Trust the Lord", x: -140, y: 40, hue: 210, icon: FaCross },
  { text: "Fear not", x: 0, y: -160, hue: 30, icon: FaShieldAlt },
  { text: "Do good", x: 160, y: 10, hue: 120, icon: FaGift },
  { text: "Forgive others", x: -70, y: 160, hue: 290, icon: FaHandHoldingHeart },
  { text: "Rejoice always", x: 100, y: 140, hue: 10, icon: FaLaughBeam },
  { text: "Walk in love", x: -180, y: -40, hue: 200, icon: FaWalking },
  { text: "Serve the Lord", x: 190, y: -10, hue: 65, icon: FaChurch },
  { text: "Seek God’s kingdom", x: -50, y: -190, hue: 255, icon: FaGlobe },
  { text: "Be still", x: 140, y: 80, hue: 180, icon: FaLeaf },
  { text: "Speak truth", x: -150, y: 90, hue: 320, icon: FaBullhorn },
  { text: "Humble yourself", x: 60, y: -140, hue: 200, icon: FaSeedling },
  { text: "Bear one another", x: 0, y: 190, hue: 25, icon: FaUsers },
  { text: "Honor your parents", x: 210, y: 120, hue: 80, icon: FaUserFriends },
  { text: "Give thanks", x: -200, y: 110, hue: 310, icon: FaHandshake },
  { text: "Love your enemies", x: 20, y: 210, hue: 260, icon: FaPeace },
  { text: "Keep His commandments", x: -90, y: -220, hue: 40, icon: FaBook },
  { text: "Be strong", x: 170, y: 200, hue: 150, icon: FaDumbbell },
  { text: "Follow Jesus", x: -230, y: 30, hue: 330, icon: FaHeart },
  { text: "Bless the poor", x: 80, y: -210, hue: 15, icon: FaCoins },
  { text: "Guard your heart", x: -180, y: 220, hue: 170, icon: FaShieldAlt },
  { text: "Speak with grace", x: 200, y: -180, hue: 60, icon: FaFeather },
  { text: "Live by faith", x: -220, y: 150, hue: 230, icon: FaFeatherAlt },
  { text: "Walk in light", x: 0, y: 230, hue: 40, icon: FaSun },
  { text: "Clothe with kindness", x: -250, y: -120, hue: 300, icon: FaTshirt },
  { text: "Endure in hope", x: 180, y: 210, hue: 120, icon: FaHourglass },
  { text: "Share your bread", x: -30, y: -260, hue: 280, icon: FaBreadSlice },
  { text: "Lift the weary", x: 220, y: 30, hue: 50, icon: FaHandsHelping },
  { text: "Stand in truth", x: -210, y: 60, hue: 200, icon: FaBalanceScale },
  { text: "Honor all people", x: 160, y: -250, hue: 90, icon: FaUsers },
  { text: "Pursue righteousness", x: -70, y: 250, hue: 340, icon: FaBookOpen },
  { text: "Show mercy", x: 210, y: 170, hue: 10, icon: FaHands },
  { text: "Be quick to listen", x: -190, y: -180, hue: 220, icon: FaHeadphones },
  { text: "Let peace rule", x: 250, y: -60, hue: 30, icon: FaPeace },
  { text: "Do not judge", x: -260, y: 80, hue: 180, icon: FaGavel },
  { text: "Resist the devil", x: 140, y: -210, hue: 320, icon: FaShieldAlt },
  { text: "Trust and obey", x: -120, y: 260, hue: 70, icon: FaHandshake },
  { text: "Love in deed", x: 230, y: 90, hue: 10, icon: FaHeart },
  { text: "Confess your sins", x: -280, y: -20, hue: 200, icon: FaPrayingHands },
  { text: "Bear good fruit", x: 160, y: 250, hue: 140, icon: FaAppleAlt },
  { text: "Keep the faith", x: -150, y: -260, hue: 310, icon: FaCross },
  { text: "Watch and pray", x: 70, y: 280, hue: 190, icon: FaEye },
  { text: "Walk humbly", x: -310, y: 40, hue: 340, icon: FaWalking },
  { text: "Encourage one another", x: 300, y: 20, hue: 85, icon: FaComments },
  { text: "Give to others", x: -60, y: 300, hue: 200, icon: FaGift },
  { text: "Seek the Lord", x: 260, y: -120, hue: 145, icon: FaSearch },
  { text: "Be content", x: -330, y: 10, hue: 260, icon: FaSmile },
  { text: "Offer thanksgiving", x: 210, y: -320, hue: 30, icon: FaHandsHelping },
  { text: "Be of courage", x: -240, y: 220, hue: 310, icon: FaHeart },
];

const HERO_WORDS = HERO_WORDS_RAW.map((word, index) => ({
  ...word,
  directionAngle: (index * 137.5) % 360,
}));

const WORD_ANIMATION_DURATION = 10000;
const WORD_PEAK_THRESHOLD = 0.3;

function Home() {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [videoQueue, setVideoQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState([]);
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isApiReady, setIsApiReady] = useState(false);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("all");
  const [showPlaylistPicker, setShowPlaylistPicker] = useState(false);
  const [showMoreApps, setShowMoreApps] = useState(false);
  const [uiHidden, setUiHidden] = useState(false);
  const [score, setScore] = useState(0);
  const [difficulty, setDifficulty] = useState(1);
  const [floatingPoints, setFloatingPoints] = useState([]);
  const [hasScored, setHasScored] = useState(false);
  const difficultyRef = useRef(1);
  const lastClickTimeRef = useRef(null);
  const floatingTimeoutsRef = useRef(new Map());
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [wordQueue, setWordQueue] = useState(() => [
    { index: 0, id: `hero-word-0`, start: Date.now() },
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const nextWordIndexRef = useRef(1);
  const wordTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const timerIntervalRef = useRef(null);
  const resetGameState = useCallback(() => {
    difficultyRef.current = 1;
    lastClickTimeRef.current = null;
    floatingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    floatingTimeoutsRef.current.clear();
    setScore(0);
    setDifficulty(1);
    setFloatingPoints([]);
    setHasScored(false);
  }, []);
  const toggleUiHidden = useCallback(() => {
    setUiHidden((prev) => {
      resetGameState();
      return !prev;
    });
  }, [resetGameState]);

  const formatTimer = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const confettiPieces = useMemo(() => {
    const colors = ["#facc15", "#22c55e", "#38bdf8", "#f472b6", "#a855f7", "#f97316"];
    return Array.from({ length: 28 }, (_, index) => ({
      id: `confetti-${index}`,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 3 + Math.random() * 2,
      size: 6 + Math.random() * 6,
      color: colors[index % colors.length],
      spin: Math.random() * 360,
    }));
  }, []);

  const playVictoryTone = useCallback(() => {
    if (typeof window === "undefined" || !(window.AudioContext || window.webkitAudioContext)) {
      return;
    }
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(520, context.currentTime);
      gainNode.gain.setValueAtTime(0.17, context.currentTime);
      oscillator.connect(gainNode);
      gainNode.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 1.3);
      oscillator.onended = () => {
        gainNode.disconnect();
        oscillator.disconnect();
        context.close();
      };
    } catch (error) {
      console.error("Unable to play celebration tone", error);
    }
  }, []);

  const clearChallengeInterval = useCallback(() => {
    if (typeof window === "undefined") return;
    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const handleChallengeStart = useCallback(() => {
    if (timerRunning) {
      clearChallengeInterval();
      setTimerRunning(false);
      setTimerSeconds(60);
      setShowScorePopup(false);
      return;
    }
    resetGameState();
    setShowScorePopup(false);
    setTimerSeconds(60);
    setTimerRunning(true);
  }, [clearChallengeInterval, resetGameState, timerRunning]);

  const handleHeaderLogoClick = useCallback(() => {
    toggleUiHidden();
  }, [toggleUiHidden]);

  const betaParam = useMemo(
    () => new URLSearchParams(location.search).get("beta") || "",
    [location.search],
  );
  const betaEnabled = ["on", "true", "1"].includes(betaParam.toLowerCase());

  const heroVideoPool = useMemo(() => {
    const uniqueMap = new Map();
    Object.values(localVideos || {}).forEach((video) => {
      if (video?.id) {
        uniqueMap.set(video.id, { id: video.id, title: video.title || "GCC Worship" });
      }
    });
    (Array.isArray(playlistsData) ? playlistsData : []).forEach((playlist) => {
      (playlist.videoIds || []).forEach((videoId) => {
        if (typeof videoId === "string" && !uniqueMap.has(videoId)) {
          uniqueMap.set(videoId, { id: videoId, title: playlist.title || "GCC Playlist" });
        }
      });
    });
    return Array.from(uniqueMap.values());
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const delay = WORD_ANIMATION_DURATION * WORD_PEAK_THRESHOLD;
    const scheduleNextWord = () => {
      wordTimerRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) return;
        setWordQueue((prev) => {
          const now = Date.now();
          const filtered = prev.filter((entry) => now - entry.start < WORD_ANIMATION_DURATION);
          const nextIndex = nextWordIndexRef.current;
          nextWordIndexRef.current = (nextIndex + 1) % HERO_WORDS.length;
          return [
            ...filtered,
            {
              index: nextIndex,
              id: `hero-word-${nextIndex}-${now}`,
              start: now,
            },
          ];
        });
        scheduleNextWord();
      }, delay);
    };
    scheduleNextWord();
    return () => {
      isMountedRef.current = false;
      if (wordTimerRef.current) {
        clearTimeout(wordTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Control" && !event.repeat) {
        toggleUiHidden();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    const headerLogo =
      typeof document !== "undefined" ? document.querySelector(".header-logo") : null;
    if (headerLogo) {
      headerLogo.addEventListener("click", handleHeaderLogoClick);
    }
    return () => {
    window.removeEventListener("keydown", handleKeyDown);
    if (headerLogo) {
      headerLogo.removeEventListener("click", handleHeaderLogoClick);
    }
  };
  }, [handleHeaderLogoClick, toggleUiHidden]);

  useEffect(() => {
    if (!timerRunning) {
      clearChallengeInterval();
      setTimerSeconds(60);
      return undefined;
    }
    if (typeof window === "undefined") return undefined;
    timerIntervalRef.current = window.setInterval(() => {
      setTimerSeconds((prev) => {
        if (prev <= 1) {
          clearChallengeInterval();
          setTimerRunning(false);
          setShowScorePopup(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearChallengeInterval();
    };
  }, [timerRunning, clearChallengeInterval]);

  useEffect(() => {
    if (showScorePopup) {
      playVictoryTone();
    }
  }, [showScorePopup, playVictoryTone]);

  useEffect(() => {
    return () => {
      floatingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      floatingTimeoutsRef.current.clear();
    };
  }, []);

  const handleHeroWordClick = useCallback(
    (event) => {
      if (!uiHidden) return;
      event.stopPropagation();
      const now = Date.now();
      setHasScored(true);
      const delta = lastClickTimeRef.current ? now - lastClickTimeRef.current : Infinity;
      const quickTap = delta < 600;
      const updatedDifficulty = quickTap
        ? Math.min(5, difficultyRef.current + 0.25)
        : Math.max(1, difficultyRef.current - 0.3);
      difficultyRef.current = updatedDifficulty;
      setDifficulty(updatedDifficulty);
      lastClickTimeRef.current = now;
      const points = Math.max(5, Math.round(10 * updatedDifficulty));
      setScore((prev) => prev + points);
      const id = `floating-score-${now}-${Math.random().toString(36).slice(2, 8)}`;
      setFloatingPoints((prev) => [
        ...prev,
        { id, value: `+${points}`, x: event.clientX, y: event.clientY },
      ]);
      const timeoutId = window.setTimeout(() => {
        setFloatingPoints((prev) => prev.filter((item) => item.id !== id));
        floatingTimeoutsRef.current.delete(id);
      }, 1200);
      floatingTimeoutsRef.current.set(id, timeoutId);
    },
    [uiHidden],
  );

  const heroVideoMap = useMemo(() => {
    const map = new Map();
    heroVideoPool.forEach((video) => map.set(video.id, video));
    return map;
  }, [heroVideoPool]);

  const playlistOptions = useMemo(() => {
    const base = Array.isArray(playlistsData) ? playlistsData : [];
    return [
      { id: "all", title: "All Clips" },
      ...base.map((playlist) => ({
        id: playlist.id,
        title: playlist.title || "GCC Playlist",
      })),
    ];
  }, [playlistsData]);

  const getPlaylistVideos = useCallback(
    (playlistId) => {
      if (!playlistId || playlistId === "all") return heroVideoPool;
      const playlist = (Array.isArray(playlistsData) ? playlistsData : []).find((item) => item.id === playlistId);
      if (!playlist) return heroVideoPool;
      const mapped =
        (playlist.videoIds || [])
          .map((videoId) => {
            if (!videoId) return null;
            return (
              heroVideoMap.get(videoId) || {
                id: videoId,
                title: playlist.title || "GCC TV",
              }
            );
          })
          .filter(Boolean) || [];
      return mapped.length ? mapped : heroVideoPool;
    },
    [heroVideoMap, heroVideoPool, playlistsData],
  );

  useEffect(() => {
    if (!heroVideoPool.length) return;
    const videos = getPlaylistVideos(selectedPlaylistId);
    const shuffled = shuffleArray(videos);
    setVideoQueue(shuffled);
    setCurrentIndex(0);
    setHistory([]);
  }, [heroVideoPool, selectedPlaylistId, getPlaylistVideos]);

  useEffect(() => {
    if (!betaEnabled) {
      setShowMoreApps(false);
    }
  }, [betaEnabled]);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeIframeAPI()
      .then(() => {
        if (!cancelled) {
          setIsApiReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsApiReady(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentVideo = videoQueue[currentIndex] || null;

  const handleNext = useCallback(() => {
    if (!videoQueue.length) return;
    setHistory((prev) => [...prev.slice(-9), currentIndex]);
    setCurrentIndex((prev) => (prev + 1) % videoQueue.length);
  }, [videoQueue.length, currentIndex]);

  const handleNextRef = useRef(handleNext);
  useEffect(() => {
    handleNextRef.current = handleNext;
  }, [handleNext]);

  const handlePrevious = useCallback(() => {
    setHistory((prev) => {
      if (!prev.length) return prev;
      const copy = [...prev];
      const lastIndex = copy.pop();
      if (typeof lastIndex === "number") {
        setCurrentIndex(lastIndex);
      }
      return copy;
    });
  }, []);

  const cyclePlaylist = useCallback(
    (direction) => {
      if (!playlistOptions.length) return;
      const currentIdx = playlistOptions.findIndex((option) => option.id === selectedPlaylistId);
      const nextIdx = (currentIdx + direction + playlistOptions.length) % playlistOptions.length;
      setSelectedPlaylistId(playlistOptions[nextIdx].id);
    },
    [playlistOptions, selectedPlaylistId],
  );

  const handlePlayPauseToggle = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
      setIsPlaying(false);
    } else {
      playerRef.current.playVideo();
      setIsPlaying(true);
    }
  };

  const handleMuteToggle = () => {
    if (!playerRef.current) {
      setIsMuted((prev) => !prev);
      return;
    }
    if (isMuted) {
      playerRef.current.unMute();
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  };

  const onPlayerReady = useCallback((event) => {
    playerRef.current = event.target;
    setPlayerReady(true);
    event.target.playVideo();
    setIsPlaying(true);
  }, []);

  const onPlayerStateChange = useCallback((event) => {
    const PlayerState = window?.YT?.PlayerState;
    if (!PlayerState) return;
    if (event.data === PlayerState.ENDED) {
      handleNextRef.current?.();
    } else if (event.data === PlayerState.PLAYING) {
      setIsPlaying(true);
    } else if (event.data === PlayerState.PAUSED || event.data === PlayerState.BUFFERING) {
      setIsPlaying(false);
    }
  }, []);

  useEffect(() => {
    if (!isApiReady) return;
    const container = playerContainerRef.current;
    if (!container) return;
    if (playerRef.current) return;

    const player = new window.YT.Player(container, {
      height: "0",
      width: "0",
      playerVars: HIDDEN_PLAYER_VARS,
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      setPlayerReady(false);
      setIsPlaying(false);
    };
  }, [isApiReady, onPlayerReady, onPlayerStateChange]);

  useEffect(() => {
    if (!playerReady) return;
    if (!currentVideo?.id) return;
    if (!playerRef.current || typeof playerRef.current.loadVideoById !== "function") return;
    try {
      playerRef.current.loadVideoById(currentVideo.id);
      if (isMuted) {
        playerRef.current.mute();
      } else {
        playerRef.current.unMute();
      }
      playerRef.current.playVideo();
      setIsPlaying(true);
    } catch (error) {
      console.error("Unable to load hero video", error);
    }
  }, [currentVideo, isMuted, playerReady]);

  useEffect(() => {
    const handleKeydown = (event) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (["input", "textarea", "select"].includes(activeTag || "")) return;
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          handlePrevious();
          break;
        case "ArrowRight":
          event.preventDefault();
          handleNext();
          break;
        case "ArrowUp":
          event.preventDefault();
          cyclePlaylist(-1);
          break;
        case "ArrowDown":
          event.preventDefault();
          cyclePlaylist(1);
          break;
        case " ":
        case "Spacebar":
          event.preventDefault();
          handlePlayPauseToggle();
          break;
        case "j":
        case "J":
          window.open(
            "https://www.youtube.com/@gcc-worship?sub_confirmation=1",
            "_blank",
            "noopener,noreferrer",
          );
          break;
        case "t":
        case "T":
          navigate("/live");
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [handleNext, handlePrevious, cyclePlaylist, handlePlayPauseToggle, navigate]);

  const currentPlaylist = playlistOptions.find((option) => option.id === selectedPlaylistId);
  const appsToRender = showMoreApps ? APP_LINKS : PRIMARY_APP_LINKS;

  const controlItems = [
    {
      id: "prev",
      label: "Prev",
      icon: FaStepBackward,
      onClick: handlePrevious,
      disabled: !history.length,
    },
    {
      id: "play",
      label: isPlaying ? "Pause" : "Play",
      icon: isPlaying ? FaPause : FaPlay,
      onClick: handlePlayPauseToggle,
      disabled: !playerReady,
    },
    {
      id: "next",
      label: "Next",
      icon: FaStepForward,
      onClick: handleNext,
      disabled: !videoQueue.length,
    },
    {
      id: "playlist",
      label: "Playlist",
      icon: FaListUl,
      onClick: () => setShowPlaylistPicker((prev) => !prev),
      disabled: false,
    },
    {
      id: "mute",
      label: isMuted ? "Muted" : "Sound",
      icon: isMuted ? FaVolumeMute : FaVolumeUp,
      onClick: handleMuteToggle,
      disabled: !playerReady,
    },
  ];

  return (
    <div className={`home-shell${uiHidden ? " ui-hidden" : ""}`}>
      <section className="home-hero" aria-label="Mission statement">
        <div className="home-hero-icon" aria-hidden="true">
          <div className="hero-word-layer" aria-hidden="true">
            {wordQueue.map((entry) => {
              const word = HERO_WORDS[entry.index];
              const IconComponent = word.icon;
              const angleRad = ((word.directionAngle || 0) * Math.PI) / 180;
              const directionX = Math.cos(angleRad);
              const directionY = Math.sin(angleRad);
              return (
                <span
                  key={entry.id}
                  className="hero-word"
                  style={{
                    "--word-direction-x": directionX.toFixed(4),
                    "--word-direction-y": directionY.toFixed(4),
                    "--word-base-x": `${word.x}px`,
                    "--word-base-y": `${word.y}px`,
                    "--word-hue": `${word.hue}`,
                  }}
                  onClick={handleHeroWordClick}
                >
                  {IconComponent && (
                    <IconComponent className="hero-word-icon" aria-hidden="true" />
                  )}
                  <span className="hero-word-text">{word.text}</span>
                </span>
              );
            })}
          </div>
          <span className="radiant-wave wave-one" aria-hidden="true" />
          <span className="radiant-wave wave-two" aria-hidden="true" />
          <span className="radiant-wave wave-three" aria-hidden="true" />
          <span className="hero-cross" aria-hidden="true">✝</span>
        </div>
      </section>



      {uiHidden && (
        <div className="floating-score-layer" aria-hidden="true">
          {floatingPoints.map((entry) => (
            <span
              key={entry.id}
              className="floating-score"
              style={{ left: entry.x, top: entry.y }}
            >
              {entry.value}
            </span>
          ))}
        </div>
      )}
      <SEO
        title="Global Christian Community - Home"
        description="Uniting believers worldwide through worship, Scripture, and prayer."
        keywords="Bible, Christian, worship, Bible study, Global Christian Community"
        url="https://globalchristiancommunity.com/"
      />
      <section id="app-grid" className="app-grid" aria-label="Community shortcuts">
        {appsToRender.map((item, index) => {
          const gradient = ICON_GRADIENTS[index % ICON_GRADIENTS.length];
          const iconBackground = {
            backgroundImage: `linear-gradient(145deg, ${gradient.start}, ${gradient.end})`,
          };
          const IconComponent = item.icon;
          const commonProps = {
            className: "app-link",
            "aria-label": item.label,
            key: item.id,
          };
          const linkContent = (
            <>
              <div className="app-icon" style={iconBackground}>
                <IconComponent aria-hidden="true" />
              </div>
              <span className="app-label">{item.label}</span>
            </>
          );
          if (item.href) {
            return (
              <a {...commonProps} href={item.href} target="_blank" rel="noopener noreferrer">
                {linkContent}
              </a>
            );
          }
          return (
            <Link {...commonProps} to={item.to}>
              {linkContent}
            </Link>
          );
        })}
      </section>
      {betaEnabled && (
        <div className="more-toggle">
          <button
            type="button"
            className="more-toggle-button"
            onClick={() => setShowMoreApps((prev) => !prev)}
            aria-expanded={showMoreApps}
            aria-controls="app-grid"
          >
            {showMoreApps ? (
              <FaChevronUp aria-hidden="true" className="more-toggle-icon expanded" />
            ) : (
              <FaChevronDown aria-hidden="true" className="more-toggle-icon" />
            )}
            <span className="sr-only">{showMoreApps ? "Collapse shortcuts" : "Show additional shortcuts"}</span>
          </button>
        </div>
      )}
      <section className="music-panel">
        <div className="music-panel-content">
          <div className="music-controls">
            {controlItems.map((control) => {
              const ControlIcon = control.icon;
              return (
                <div className="control-card" key={control.id}>
                  <button
                    type="button"
                    onClick={control.onClick}
                    disabled={control.disabled}
                    className="control-button"
                  >
                    <ControlIcon className="control-icon" aria-hidden="true" />
                  </button>
                  <span className="control-caption">{control.label}</span>
                </div>
              );
            })}
          </div>
          {showPlaylistPicker && (
            <div className="playlist-popover control-popover" role="dialog" aria-label="Choose playlist">
              <ul>
                {playlistOptions.map((option) => (
                  <li key={option.id}>
                    <button
                      type="button"
                      className={`playlist-option ${selectedPlaylistId === option.id ? "active" : ""}`}
                      onClick={() => {
                        setSelectedPlaylistId(option.id);
                        setShowPlaylistPicker(false);
                      }}
                    >
                      {option.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="now-playing-wrapper">
          <p className="track-title">{currentVideo?.title || "Loading GCC TV..."}</p>
          <p className="playlist-subtitle">{currentPlaylist?.title || "All Clips"}</p>
          </div>
        </div>
      </section>
      <section className="home-feature-video" aria-label="Featured GCC video">
        <div className="feature-video-shell">
          <div className="feature-video-tv">
            <div className="feature-video-screen">
              <iframe
                src="https://www.youtube.com/embed/0Mk7q6i3kHo?rel=0&autoplay=1&mute=1&playsinline=1&loop=1&playlist=0Mk7q6i3kHo"
                title="Global Christian Community platform overview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="feature-video-base" aria-hidden="true" />
          </div>
        </div>
      </section>
      <div ref={playerContainerRef} className="hidden-player" aria-hidden="true" />
      {uiHidden && (
        <section className="challenge-control-panel" aria-live="polite">
          <div className="challenge-panel-main">
            <button
              type="button"
              className="challenge-start-button"
              onClick={handleChallengeStart}
              disabled={timerRunning}
            >
              {timerRunning ? "Running" : "Play"}
            </button>
            <div className="challenge-timer">
              <span className="challenge-timer-label">Minute</span>
              <span className="challenge-timer-value">{formatTimer(timerSeconds)}</span>
            </div>
          </div>
          <div className="challenge-stats">
            <div className="challenge-stat">
              <span className="challenge-stat-label">Score</span>
              <span className="challenge-stat-value">{score.toLocaleString()}</span>
            </div>
            <div className="challenge-stat">
              <span className="challenge-stat-label">Intensity</span>
              <span className="challenge-stat-value">{difficulty.toFixed(2)}×</span>
            </div>
          </div>
        </section>
      )}
      {showScorePopup && (
        <div className="challenge-overlay" role="dialog" aria-label="Challenge score">
          <div className="confetti-wrapper" aria-hidden="true">
            {confettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="confetti-piece"
                style={{
                  left: `${piece.left}%`,
                  animationDelay: `${piece.delay}s`,
                  animationDuration: `${piece.duration}s`,
                  width: `${piece.size}px`,
                  height: `${piece.size * 1.25}px`,
                  backgroundColor: piece.color,
                  transform: `rotate(${piece.spin}deg)`,
                }}
              />
            ))}
          </div>
          <div className="score-popup-card">
            <p className="score-popup-title">One Minute of Radiance</p>
            <p className="score-popup-score" aria-live="polite">
              {score.toLocaleString()}
            </p>
            <p className="score-popup-subtext">
              Points captured during the hidden tap burst
            </p>
            <button
              type="button"
              className="score-popup-close"
              onClick={() => setShowScorePopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
